import { CMSComponent, type CMSComponentProps } from "../engine/CMSComponent.tsx";
import RichText from "../engine/RichText.tsx";
import { isCMSNode, isRichText, type CMSNode } from "../types.ts";
import { ChevronRight, useLink } from "./KitButton.tsx";
import { SectionShell } from "./Section.tsx";

/* Blocks designed for bytecode.ca: link tiles, product cards and long-form articles. */

const str = (value: unknown) => (typeof value === "string" ? value : undefined);
const nodes = (value: unknown) => (Array.isArray(value) ? value.filter(isCMSNode) : []);

function Tile({ tile }: { tile: CMSNode }) {
  return (
    <a className="cms-tile cms-reveal" {...useLink(str(tile.href))}>
      <p className="cms-label">{str(tile.label)}</p>
      <p className="cms-tile__text">{str(tile.text)}</p>
      <span className="cms-arrow-link">
        <ChevronRight />
        {str(tile.linkText)}
      </span>
    </a>
  );
}

export function LinkTiles({ node }: CMSComponentProps) {
  return (
    <SectionShell node={node} className="cms-tiles">
      <div className="cms-tiles__grid">
        {nodes(node.collection).map((tile, index) => (
          <Tile key={tile.id ?? index} tile={tile} />
        ))}
      </div>
    </SectionShell>
  );
}

function Product({ product }: { product: CMSNode }) {
  const link = useLink(str(product.href));
  return (
    <article className="cms-product cms-reveal">
      {str(product.tag) && <p className="cms-product__tag">{str(product.tag)}</p>}
      <h3 className="cms-product__name">{str(product.name)}</h3>
      {str(product.tagline) && <p className="cms-product__tagline">{str(product.tagline)}</p>}
      {str(product.description) && (
        <p className="cms-product__description">{str(product.description)}</p>
      )}
      {link.href && (
        <a className="cms-product__link" {...link}>
          <ChevronRight />
          {str(product.linkText)}
        </a>
      )}
    </article>
  );
}

export function ProductCards({ node }: CMSComponentProps) {
  return (
    <SectionShell node={node} className="cms-products">
      <div className="cms-section-heading cms-reveal">
        <CMSComponent node={node.textGroup} />
      </div>
      <div className="cms-products__list">
        {nodes(node.collection).map((product, index) => (
          <Product key={product.id ?? index} product={product} />
        ))}
      </div>
    </SectionShell>
  );
}

export function Article({ node }: CMSComponentProps) {
  return (
    <SectionShell node={node} className="cms-article">
      <h1 className="cms-article__title">{str(node.title)}</h1>
      {str(node.subtitle) && <p className="cms-article__subtitle">{str(node.subtitle)}</p>}
      {isRichText(node.body) && (
        <div className="cms-article__body">
          <RichText document={node.body} />
        </div>
      )}
    </SectionShell>
  );
}
